//#region Imports Packages and Libs

import { MESSAGE_COMPONENT_TYPE } from "../../../enums";
import { MessageStructureComponent } from "./../../../types";

//#endregion

//#region ComponentMapper Class

/**
 * ComponentMapper is responsible for mapping HTML elements (such as links)
 * to their corresponding MessageStructureComponent instances. It provides
 * utility methods to retrieve components based on DOM structure and to
 * generate fallback components when necessary.
 */
export class ComponentMapper {
	/**
	 * Retrieves the component associated with a specific link element.
	 * This method searches for the closest parent <p> element with a data-index attribute,
	 * parses the index, and returns the corresponding component from the components array.
	 *
	 * @param link - The HTML element representing the link.
	 * @param components - An array of MessageStructureComponent instances.
	 *
	 * @returns The corresponding MessageStructureComponent if found; otherwise, null.
	 */
	static getComponentForLink(
		link: HTMLElement,
		components: MessageStructureComponent[]
	): MessageStructureComponent | null {
		try {
			// Find the closest parent <p> element with a data-index attribute
			const wrapper = link.closest("p[data-index]") as HTMLElement;
			if (!wrapper) return null;

			// Parse the data-index attribute to get the component index
			const index = parseInt(wrapper.dataset.index!, 10);

			// Validate the index and components array
			if (isNaN(index) || index < 0 || index >= components.length) return null;
			if (!components || !Array.isArray(components)) return null;

			// Return the corresponding component or null if not found
			const component = components[index] || null;
			return structuredClone(component); // Clone the component deeply to avoid mutating the original
		} catch (ex) {
			console.log("[component_mapper.ts] An error has occurred \n-> Inside getComponentForLink -> Error: ", ex);
			return null;
		}
	}

	/**
	 * Creates a fallback component with HTML content.
	 * This is used when no specific component is found for a link.
	 *
	 * @param html - The HTML content to be included in the fallback component.
	 * @returns A MessageStructureComponent of type 'html' containing the provided HTML content.
	 */
	static createFallbackComponent(html: string): MessageStructureComponent {
		return {
			type: "html" as MESSAGE_COMPONENT_TYPE,
			content: html,
		};
	}
}

//#endregion
